/**
 * `sanitiseForLLM` — defence-in-depth sanitiser applied at every string field
 * that lands in `getSnapshot()` output.
 *
 * Why: every snapshot field is intended to be templated into an LLM system
 * prompt. Multiple snapshot ingress points are attacker-influenceable:
 * URL query params, `document.referrer`, DOM `id` attributes, Segment event
 * names, etc. (see `.agents/security-review.md` F1 + F3). Without
 * sanitisation, an attacker can inject `</system>` markers or other chat-
 * template tokens and escape the host's prompt scaffold.
 *
 * The sanitiser is intentionally narrow:
 *   1. Length-cap (configurable per call; sensible defaults per field type).
 *   2. Strip C0/C1 control characters except `\t` and `\n`.
 *   3. Strip zero-width characters and bidi-override / format controls
 *      (these are otherwise invisible to a human reviewer and can be used
 *      to smuggle markers past visual inspection).
 *   4. Replace prompt-control markers (`</system>`, `<|im_end|>`, common
 *      chat-template tokens) with `?`. We don't attempt to be exhaustive —
 *      this is a deny-list to raise the bar, not a parser.
 *
 * Future M1.5++ can tighten further (e.g. fenced-block escaping at the
 * consumer boundary). For now, the README will also document that
 * downstream consumers should wrap snapshot output in a fenced JSON block.
 */

/** Maximum total characters allowed in any single snapshot string field. */
export const SANITISE_DEFAULT_LIMIT = 256;

/** Tighter cap for selector-like fields (CSS selectors, event names). */
export const SANITISE_SELECTOR_LIMIT = 64;

/** Cap for URL-like fields (URLs are routinely longer than 256 chars). */
export const SANITISE_URL_LIMIT = 512;

/**
 * Substrings replaced with `?` to neutralise chat-template injections.
 * Matched case-insensitively. Not a parser; a deny-list to raise the bar.
 *
 * The list covers:
 *   - Common HTML/markdown role tags an LLM may interpret as prompt scaffold
 *     (`</system>`, `</user>`, `</assistant>`).
 *   - ChatML-style special tokens (`<|im_start|>`, `<|im_end|>`).
 *   - Anthropic-style human/assistant turn markers.
 *   - Common context-fence tokens.
 */
const PROMPT_MARKERS: readonly RegExp[] = [
  /<\/?\s*system\s*>/gi,
  /<\/?\s*user\s*>/gi,
  /<\/?\s*assistant\s*>/gi,
  /<\/?\s*context\s*>/gi,
  /<\|im_start\|>/gi,
  /<\|im_end\|>/gi,
  /<\|endoftext\|>/gi,
  /\[INST\]/gi,
  /\[\/INST\]/gi,
  /<<SYS>>/gi,
  /<<\/SYS>>/gi,
  // Anthropic-style human/assistant markers (rare in real text; common in
  // jailbreak attempts).
  /\n\nHuman:\s/gi,
  /\n\nAssistant:\s/gi,
];

/**
 * Strip control characters and zero-width / bidi-format characters.
 *
 * Keeps `\t` (U+0009) and `\n` (U+000A) for legibility — these don't help
 * an attacker escape a prompt scaffold any more than a space does.
 */
function stripControls(input: string): string {
  let out = '';
  for (let i = 0; i < input.length; i += 1) {
    const code = input.charCodeAt(i);
    // Allow tab and newline.
    if (code === 0x09 || code === 0x0a) {
      out += input[i];
      continue;
    }
    // C0 controls (U+0000–U+001F) and DEL (U+007F).
    if (code < 0x20 || code === 0x7f) continue;
    // C1 controls (U+0080–U+009F).
    if (code >= 0x80 && code <= 0x9f) continue;
    // Zero-width and bidi-format characters.
    //   U+200B ZERO WIDTH SPACE
    //   U+200C ZERO WIDTH NON-JOINER
    //   U+200D ZERO WIDTH JOINER
    //   U+200E LEFT-TO-RIGHT MARK
    //   U+200F RIGHT-TO-LEFT MARK
    //   U+202A–U+202E bidi embedding/override controls
    //   U+2060 WORD JOINER
    //   U+2066–U+2069 isolate controls
    //   U+FEFF ZERO WIDTH NO-BREAK SPACE (BOM)
    if (
      code === 0x200b ||
      code === 0x200c ||
      code === 0x200d ||
      code === 0x200e ||
      code === 0x200f ||
      (code >= 0x202a && code <= 0x202e) ||
      code === 0x2060 ||
      (code >= 0x2066 && code <= 0x2069) ||
      code === 0xfeff
    ) {
      continue;
    }
    out += input[i];
  }
  return out;
}

/**
 * Sanitise an arbitrary string for safe inclusion in an LLM prompt context.
 *
 * Returns the sanitised string, length-capped at `limit`.
 * If the input is not a string, the input is returned unchanged (the caller
 * is expected to only invoke this for string-typed snapshot fields).
 */
export function sanitiseForLLM(
  value: string,
  limit: number = SANITISE_DEFAULT_LIMIT
): string {
  let out = stripControls(value);
  for (const marker of PROMPT_MARKERS) {
    out = out.replace(marker, '?');
  }
  if (out.length > limit) {
    out = out.slice(0, limit);
  }
  return out;
}

